// src/components/AddImpersonatingDomainModal.js
import React, { useState } from 'react';
import { X, Globe, AlertCircle } from 'lucide-react';

const DOMAIN_TYPES = [
  'Typosquatting',
  'Homograph Attack',
  'Combosquatting',
  'Brand Jacking',
  'Phishing',
  'Look-alike Domain',
  'Other'
];

const RISK_LEVELS = [
  'Critical',
  'High',
  'Medium',
  'Low',
  'Informational'
];

const STATUS_OPTIONS = [
  'Active',
  'Monitoring',
  'Takedown Requested',
  'Takedown Successful',
  'Resolved',
  'False Positive'
];

const AddImpersonatingDomainModal = ({ isOpen, onClose, onDomainAdded, companies }) => {
  const [formData, setFormData] = useState({
    companyId: '',
    domain: '',
    type: 'Typosquatting',
    status: 'Active',
    risk: 'Medium',
    registered: '',
    registrar: '',
    similarity: '',
    notes: '',
    ip_address: '',
    screenshot_url: '',
    urlscan_result_url: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const resetForm = () => {
    setFormData({
      companyId: '',
      domain: '',
      type: 'Typosquatting',
      status: 'Active',
      risk: 'Medium',
      registered: '',
      registrar: '',
      similarity: '',
      notes: '',
      ip_address: '',
      screenshot_url: '',
      urlscan_result_url: ''
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const token = await window.Clerk?.session?.getToken();
      
      if (!token) {
        throw new Error('Authentication required. Please log in again.');
      }

      if (!formData.companyId || !formData.domain) {
        throw new Error('Company and domain are required');
      }

      // Find the company slug from numeric_id
      const selectedCompany = companies.find(c => c.numeric_id === parseInt(formData.companyId));
      if (!selectedCompany) {
        throw new Error('Invalid company selected');
      }

      const response = await fetch(`/api/companies/${selectedCompany.id}/impersonating-domains`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          domain: formData.domain,
          type: formData.type,
          status: formData.status,
          risk: formData.risk,
          registered: formData.registered || null,
          registrar: formData.registrar || null,
          similarity: formData.similarity || null,
          notes: formData.notes || null,
          ip_address: formData.ip_address || null,
          screenshot_url: formData.screenshot_url || null,
          urlscan_result_url: formData.urlscan_result_url || null
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create impersonating domain');
      }

      console.log('Impersonating domain created:', data);
      
      if (onDomainAdded) {
        onDomainAdded(data);
      }

      resetForm();
      onClose();
      
      alert(`✅ Impersonating domain "${data.domain}" (${data.domain_id}) created successfully!`);

    } catch (err) {
      console.error('Error creating impersonating domain:', err);
      setError(err.message || 'Failed to create impersonating domain');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-purple-500/30 rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-800/50 bg-gradient-to-r from-purple-900/30 to-slate-900/30">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-100 flex items-center gap-2">
                <Globe className="w-5 h-5 text-purple-400" />
                Add Impersonating Domain
              </h2>
              <p className="text-sm text-slate-400 mt-1">
                Register a new impersonating/phishing domain
              </p>
            </div>
            <button
              onClick={() => {
                resetForm();
                onClose();
              }}
              className="p-2 hover:bg-slate-800/50 rounded-lg transition-all group"
              disabled={loading}
            >
              <X className="w-5 h-5 text-slate-400 group-hover:text-slate-200" />
            </button>
          </div>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          
          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-400">Error</p>
                <p className="text-sm text-red-300 mt-1">{error}</p>
              </div>
            </div>
          )}

          <div className="space-y-6">
            
            {/* Company Selection */}
            <div>
              <h3 className="text-sm font-semibold text-slate-300 mb-4">
                Company Assignment
              </h3>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-2">
                  Company <span className="text-red-400">*</span>
                </label>
                <select
                  value={formData.companyId}
                  onChange={(e) => setFormData({ ...formData, companyId: e.target.value })}
                  className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                  required
                  disabled={loading}
                >
                  <option value="">Select a company</option>
                  {companies?.map((company) => (
                    <option key={company.numeric_id} value={company.numeric_id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Domain Information */}
            <div>
              <h3 className="text-sm font-semibold text-slate-300 mb-4">
                Domain Information
              </h3>
              
              <div className="space-y-4">
                
                {/* Domain */}
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-2">
                    Domain <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.domain}
                    onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                    className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all font-mono"
                    placeholder="e.g., glareindustriess.com"
                    required
                    disabled={loading}
                  />
                </div>

                {/* Type, Status, Risk */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Type
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      disabled={loading}
                    >
                      {DOMAIN_TYPES.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Status
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      disabled={loading}
                    >
                      {STATUS_OPTIONS.map((status) => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Risk Level
                    </label>
                    <select
                      value={formData.risk}
                      onChange={(e) => setFormData({ ...formData, risk: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      disabled={loading}
                    >
                      {RISK_LEVELS.map((risk) => (
                        <option key={risk} value={risk}>{risk}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Registration Details */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Registration Date
                    </label>
                    <input
                      type="date"
                      value={formData.registered}
                      onChange={(e) => setFormData({ ...formData, registered: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Registrar
                    </label>
                    <input
                      type="text"
                      value={formData.registrar}
                      onChange={(e) => setFormData({ ...formData, registrar: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      placeholder="e.g., Namecheap, GoDaddy"
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Phishing Score & IP */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Phishing Score
                    </label>
                    <input
                      type="text"
                      value={formData.similarity}
                      onChange={(e) => setFormData({ ...formData, similarity: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      placeholder="e.g., 95%"
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      IP Address
                    </label>
                    <input
                      type="text"
                      value={formData.ip_address}
                      onChange={(e) => setFormData({ ...formData, ip_address: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all font-mono"
                      placeholder="e.g., 185.234.219.45"
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Screenshot Evidence (optional) */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Screenshot URL
                    </label>
                    <input
                      type="url"
                      value={formData.screenshot_url}
                      onChange={(e) => setFormData({ ...formData, screenshot_url: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all font-mono"
                      placeholder="https://urlscan.io/screenshots/<uuid>.png"
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      urlscan Report URL
                    </label>
                    <input
                      type="url"
                      value={formData.urlscan_result_url}
                      onChange={(e) => setFormData({ ...formData, urlscan_result_url: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all font-mono"
                      placeholder="https://urlscan.io/result/<uuid>/"
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-2">
                    Notes
                  </label>
                  <textarea
                    rows={4}
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                    placeholder="Additional details about this impersonating domain..."
                    disabled={loading}
                  />
                </div>

              </div>
            </div>

          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-800/50 bg-slate-900/50 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => {
              resetForm();
              onClose();
            }}
            disabled={loading}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-sm font-medium text-slate-200 transition-all"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={loading || !formData.companyId || !formData.domain}
            className="px-4 py-2 bg-purple-500 hover:bg-purple-600 disabled:bg-slate-700 disabled:text-slate-500 disabled:cursor-not-allowed rounded-lg text-sm font-medium text-white transition-all flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Creating...
              </>
            ) : (
              'Create Domain Entry'
            )}
          </button>
        </div>

      </div>
    </div>
  );
};

export default AddImpersonatingDomainModal;